import { useDispatch, useSelector } from 'react-redux';
import React, { useState, useRef, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';

import './index.css';
import constants from '../../constants';
import GmailConnectModal from './GmailConnectModal';
import ConnectionStatus from './ConnectionStatus';
import IntegrationToggle from './IntegrationToggle';
import LinkedInLoginModal from './LinkedInLoginModal';
import { deleteIntegration, getIntegrations, testIntegrationConnection, setPrimaryIntegration, testSmtpConnection } from '../../store/Integration';

/**
 * IntegrationBox - Renders a single integration box dynamically (supports multiple accounts)
 */
const IntegrationBox = ({ tool, integrations = [], canManageIntegrations }) => {

    const dispatch = useDispatch();
    const integrationTest = useSelector(state => state['Integration']?.['integrationTest']) || {};

    const isGmail = tool['name'] === 'Gmail';
    const isLinkedIn = tool['name'] === 'LinkedIn';

    const [errorMessage, setErrorMessage] = useState('');
    const [hasMoreBelow, setHasMoreBelow] = useState(false);
    const [showErrorModal, setShowErrorModal] = useState(false);
    const [showDisconnectModal, setShowDisconnectModal] = useState(false);
    const [disconnectingIntegration, setDisconnectingIntegration] = useState(null);

    const scrollRef = useRef(null);
    const [gmailOpen, setGmailOpen] = useState(false);
    const [smtpTesting, setSmtpTesting] = useState({});

    // LinkedIn modal state
    const [liModal, setLiModal] = useState({ open: false, mode: 'create', integrationId: null, accountIdentifier: null });
    const openLiModal = (mode, integrationId = null, accountIdentifier = null) => setLiModal({ open: true, mode, integrationId, accountIdentifier });
    const closeLiModal = () => setLiModal({ open: false, mode: 'create', integrationId: null, accountIdentifier: null });

    // Poll every 3s while any LinkedIn account is in a transient state
    useEffect(() => {
        if (!isLinkedIn) return;
        const hasTransient = integrations.some(i => {
            const s = i['linkedinSession']?.['status'];
            return s === 'login_in_progress' || s === 'awaiting_verification';
        });
        if (!hasTransient) return;
        const interval = setInterval(() => dispatch(getIntegrations()), 3000);
        return () => clearInterval(interval);
    }, [isLinkedIn, integrations, dispatch]);

    // Auto-open verify modal when an account transitions to awaiting_verification
    useEffect(() => {
        if (!isLinkedIn || liModal['open']) return;
        const needsVerify = integrations.find(i => i['linkedinSession']?.['status'] === 'awaiting_verification');
        if (needsVerify) openLiModal('verify', needsVerify['_id']);
    }, [isLinkedIn, integrations]);

    const checkScroll = () => {
        const el = scrollRef.current;
        if (!el) return;
        setHasMoreBelow(el.scrollTop + el.clientHeight < el.scrollHeight - 4);
    };

    useEffect(() => {
        checkScroll();
    }, [integrations]);

    const hasConnections = integrations.length > 0;

    const handleDisconnect = async () => {
        if (!disconnectingIntegration || !disconnectingIntegration['_id']) return;
        try {
            await dispatch(deleteIntegration(disconnectingIntegration['_id']));
            dispatch(getIntegrations());
        } catch (error) {
            console.error(`Failed to disconnect ${tool['name']}:`, error);
            setErrorMessage(`Failed to disconnect ${tool['name']}. Please try again.`);
            setShowErrorModal(true);
        }
        setShowDisconnectModal(false);
        setDisconnectingIntegration(null);
    };

    const openDisconnectModal = (integration) => {
        setShowDisconnectModal(true);
        setDisconnectingIntegration(integration);
    };

    const handleTestConnection = (integrationId) => {
        if (!integrationId || integrationTest[integrationId]?.['loading']) return;
        dispatch(testIntegrationConnection(integrationId));
    };

    const handleSetPrimary = (integrationId) => dispatch(setPrimaryIntegration(integrationId));

    const handleTestSmtp = (integrationId) => {
        if (smtpTesting[integrationId]) return;
        setSmtpTesting(s => ({ ...s, [integrationId]: true }));
        dispatch(testSmtpConnection(
            integrationId,
            () => setSmtpTesting(s => ({ ...s, [integrationId]: false })),
            () => setSmtpTesting(s => ({ ...s, [integrationId]: false }))
        ));
    };

    return (
        <div className='col-xl-4 col-lg-6 col-md-6 col-sm-12 integration-col'>
            <div className='integrations-box'>
                <div className='tool-header'>
                    <div className='icon'>
                        {tool['logo'] ? <img src={tool['logo']} alt={tool['name']} className="tool-logo" /> : null}
                    </div>
                    <h4 className='tool-name'>{tool['name']}</h4>
                </div>
                <div className='text-box'><p>{tool['description']}</p></div>
                <div className='bottom-btns-area bottom-btns-area-column'>
                    {hasConnections ? (
                        <>
                            <div className="accounts-scroll-wrapper">
                                <div
                                    ref={scrollRef}
                                    onScroll={checkScroll}
                                    className="accounts-container"
                                >
                                    {integrations.map((integration, index) => {
                                        const accountId = integration['accountIdentifier'];
                                        const isLastItem = index === integrations.length - 1;
                                        const liStatus = integration['linkedinSession']?.['status'] || 'needs_login';
                                        const liStatusClass = constants['linkedInStatusClass'][liStatus] || 'li-status-needs-login';
                                        const liStatusLabel = constants['linkedInStatusLabels'][liStatus] || 'Needs Login';
                                        const needsReconnect = isGmail && integration['connectionType'] === 'oauth';
                                        return (
                                            <div
                                                key={integration['_id']}
                                                className="account-row"
                                                style={isLastItem ? { borderBottom: 'none' } : undefined}
                                            >
                                                <div className="account-info">
                                                    {isLinkedIn
                                                        ? <span className={`li-status-dot ${liStatusClass}`}></span>
                                                        : <i className={`fa ${needsReconnect ? 'fa-exclamation-circle account-checkmark-warning' : 'fa-check-circle account-checkmark'}`}></i>
                                                    }
                                                    <div className="account-info-text">
                                                        <div className="account-identifier-row">
                                                            <span className="account-identifier">{isLinkedIn ? (integration['name'] || accountId || 'LinkedIn Account') : (accountId || 'Connected')}</span>
                                                            {isLinkedIn && <span className={`li-status-pill ${liStatusClass}`}>{liStatusLabel}</span>}
                                                            {needsReconnect && <span className="reconnect-required-pill">Reconnect Required</span>}
                                                            {integration['isPrimary'] && <span className="primary-badge">(Default)</span>}
                                                        </div>
                                                        {isLinkedIn ? (
                                                            <span className="account-meta">
                                                                {accountId && <span>{accountId}</span>}
                                                                {integration['createdAt'] && <span>{accountId ? ' · ' : ''}Connected {new Date(integration['createdAt']).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}
                                                            </span>
                                                        ) : (
                                                            <span className="account-meta">
                                                                {integration['connectionType'] === 'smtp' && <span className="connection-type-badge">App Password</span>}
                                                                {integration['connectionType'] === 'workspace' && <span className="connection-type-badge">Workspace</span>}
                                                                {integration['userId']?.['firstName']
                                                                    ? `${integration['userId']['firstName']} ${integration['userId']['lastName'] || ''}`.trim()
                                                                    : integration['userId']?.['email'] || 'Unknown'
                                                                }
                                                                {integration['createdAt'] && ` · ${new Date(integration['createdAt']).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}`}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>

                                                <div className="account-actions">
                                                    {needsReconnect && canManageIntegrations && (
                                                        <button className="action-btn action-reconnect" title="OAuth no longer supported — reconnect via App Password" onClick={() => setGmailOpen(true)}>
                                                            <i className='fa fa-refresh'></i> Reconnect
                                                        </button>
                                                    )}
                                                    {isLinkedIn && canManageIntegrations && liStatus === 'needs_login' && (
                                                        <button className="action-btn li-action-login" title="Login to LinkedIn" onClick={() => openLiModal('login', integration['_id'], integration['accountIdentifier'])}>
                                                            <i className='fa fa-unlock'></i>
                                                        </button>
                                                    )}
                                                    {isLinkedIn && liStatus === 'login_in_progress' && (
                                                        <button className="action-btn" title="Logging in..." disabled>
                                                            <i className='fa fa-spinner fa-spin'></i>
                                                        </button>
                                                    )}
                                                    {isLinkedIn && canManageIntegrations && liStatus === 'awaiting_verification' && (
                                                        <button className="action-btn li-action-verify" title="Enter Verification Code" onClick={() => openLiModal('verify', integration['_id'])}>
                                                            <i className='fa fa-exclamation-circle'></i>
                                                        </button>
                                                    )}
                                                    {!isLinkedIn && tool['authMethod'] === 'apiKey' && (
                                                        <button
                                                            className="action-btn"
                                                            title="Test Connection"
                                                            disabled={integrationTest[integration['_id']]?.['loading']}
                                                            onClick={() => handleTestConnection(integration['_id'])}
                                                        >
                                                            {integrationTest[integration['_id']]?.['loading']
                                                                ? <i className='fa fa-spinner fa-spin'></i>
                                                                : <i className='fa fa-plug'></i>}
                                                        </button>
                                                    )}
                                                    {(integration['connectionType'] === 'smtp' || integration['connectionType'] === 'workspace') && (
                                                        <button
                                                            className="action-btn"
                                                            title="Test SMTP Connection"
                                                            disabled={smtpTesting[integration['_id']]}
                                                            onClick={() => handleTestSmtp(integration['_id'])}
                                                        >
                                                            {smtpTesting[integration['_id']]
                                                                ? <i className='fa fa-spinner fa-spin'></i>
                                                                : <i className='fa fa-plug'></i>}
                                                        </button>
                                                    )}
                                                    {integrations.length > 1 && canManageIntegrations && (
                                                        <button
                                                            title={integration['isPrimary'] ? "Default Account" : "Set as Default"}
                                                            onClick={() => !integration['isPrimary'] && handleSetPrimary(integration['_id'])}
                                                            className={`primary-select-btn ${integration['isPrimary'] ? 'is-primary' : ''}`}
                                                        >
                                                            <span className={`primary-indicator ${integration['isPrimary'] ? 'active' : ''}`}>
                                                                {integration['isPrimary'] && <i className='fa fa-check'></i>}
                                                            </span>
                                                        </button>
                                                    )}
                                                    {!isLinkedIn && integration['isOwned'] && canManageIntegrations &&
                                                        <IntegrationToggle
                                                            disabled={false}
                                                            integrationName={tool['name']}
                                                            integrationId={integration['_id']}
                                                            onUpdate={() => dispatch(getIntegrations())}
                                                            visibilityType={integration['visibilityType'] || 'private'}
                                                        />
                                                    }
                                                    {canManageIntegrations &&
                                                        <button
                                                            title="Remove Account"
                                                            className="action-btn"
                                                            onClick={() => openDisconnectModal(integration)}
                                                        >
                                                            <i className='fa fa-trash'></i>
                                                        </button>
                                                    }
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                                {hasMoreBelow && <div className="accounts-fade-indicator" />}
                            </div>

                            {canManageIntegrations && (
                                <div className="accounts-add-footer">
                                    {isLinkedIn ? (
                                        <button className="btn-style-new btn-sm" onClick={() => openLiModal('create')}>
                                            <i className='fa fa-plus mr-1'></i> Add Account
                                        </button>
                                    ) : isGmail ? (
                                        <button data-testid="gmail-add-another-account" className="add-account-btn" onClick={() => setGmailOpen(true)}>
                                            <i className='fa fa-plus'></i> Add Another Account
                                        </button>
                                    ) : (
                                        <ConnectionStatus
                                            toolName={tool['name']}
                                            onConnect={() => dispatch(getIntegrations())}
                                            showAddOnly={true}
                                        />
                                    )}
                                </div>
                            )}
                        </>
                    ) : (
                        canManageIntegrations ? (
                            isLinkedIn ? (
                                <button className="btn-style-new" onClick={() => openLiModal('create')}>
                                    <i className='fa fa-plus mr-1'></i> Add LinkedIn Account
                                </button>
                            ) : isGmail ? (
                                <button data-testid="gmail-connect" className="btn-style-new" onClick={() => setGmailOpen(true)}>
                                    <i className='fa fa-plug spinner-margin'></i> Connect Gmail
                                </button>
                            ) : (
                                <ConnectionStatus
                                    toolName={tool['name']}
                                    onConnect={() => dispatch(getIntegrations())}
                                    showConnectOnly={true}
                                />
                            )
                        ) : <span className="contact-admin-text">Contact an admin to connect integrations</span>
                    )}
                </div>
            </div>

            {/* Disconnect Confirmation Modal */}
            <Modal
                isOpen={showDisconnectModal}
                toggle={() => { setShowDisconnectModal(false); setDisconnectingIntegration(null); }}
                className="main-modal new-user-modal modal-dialog-centered"
            >
                <ModalHeader toggle={() => { setShowDisconnectModal(false); setDisconnectingIntegration(null); }}>
                    <h3>Disconnect {tool['name']}?</h3>
                </ModalHeader>
                <ModalBody>
                    <div className="modal-area mt-4">
                        <h5 className="mb-0">Are you sure you want to disconnect this {tool['name']} account?</h5>
                        {disconnectingIntegration && (
                            <p className="modal-account-info">
                                <i className='fa fa-info-circle'></i>
                                Account: {disconnectingIntegration?.['accountIdentifier'] || 'N/A'}
                            </p>
                        )}
                        <p className="modal-warning-text">
                            This will remove all {tool['name']} functionality from your workflows and automation.
                        </p>
                    </div>
                    <div className="group-btns mt-4 d-flex justify-content-end">
                        <button
                            className="btn-style-border mr-2"
                            onClick={() => { setShowDisconnectModal(false); setDisconnectingIntegration(null); }}
                        >
                            Cancel
                        </button>
                        <button className="btn-style-new" onClick={handleDisconnect}>
                            <i className='icon fa fa-trash'></i> Disconnect
                        </button>
                    </div>
                </ModalBody>
            </Modal>

            {/* Error Modal */}
            <Modal
                isOpen={showErrorModal}
                toggle={() => setShowErrorModal(false)}
                className="main-modal new-user-modal modal-dialog-centered"
            >
                <ModalHeader toggle={() => setShowErrorModal(false)}>
                    <h3>Error</h3>
                </ModalHeader>
                <ModalBody>
                    <div className="modal-area">
                        <p>{errorMessage}</p>
                    </div>
                </ModalBody>
                <ModalFooter>
                    <button className="btn-style-new" onClick={() => setShowErrorModal(false)}>
                        OK
                    </button>
                </ModalFooter>
            </Modal>

            {/* LinkedIn Login / Verify Modal */}
            {isLinkedIn && (
                <LinkedInLoginModal
                    isOpen={liModal['open']}
                    toggle={closeLiModal}
                    mode={liModal['mode']}
                    integrationId={liModal['integrationId']}
                    accountIdentifier={liModal['accountIdentifier']}
                    onDone={() => dispatch(getIntegrations())}
                />
            )}

            {isGmail && (
                <GmailConnectModal
                    isOpen={gmailOpen}
                    toggle={() => setGmailOpen(false)}
                    onDone={() => dispatch(getIntegrations())}
                />
            )}
        </div>
    );
};

export default IntegrationBox;
